/**
 * Anchored Rows Type Definitions
 * 
 * Types for the chapter-level, IndexedDB-backed alignment registry.
 * This is the authoritative source of confirmed alignments, separate from matrix sessions.
 */

/**
 * Mapping of a witness unit to an anchored row
 */
export interface UnitMapping {
    witnessId: string;
    unitUuid: string;              // Points to unit in segmentation result
    unitLabel?: string;            // Cached label for display
    textSnapshot?: string;         // Cached text at anchor time
    confidence?: number;
    translationStatus?: 'mapped' | 'gap' | 'uncertain' | 'transposed';
    transpositionOriginRowId?: string; // Legacy provenance read from historical mappings; new edits do not write or follow it
    excluded?: boolean;            // If true, treat as a deliberate gap for this witness
    addedAt: string;               // ISO timestamp
}

export type CollationRowTitleOrigin = 'human' | 'import' | 'model';

export interface CollationRowTitleLabel {
    text: string;
    language?: string;
    role: 'primary' | 'parallel';
}

export interface CollationRowTitleProvenance {
    sourceKind?: 'html-sidecar' | 'pi-structured-generation';
    sourceName?: string;
    sourceHash?: string;
    sourceUnitId?: string;
    generationId?: string;
    provider?: string;
    modelId?: string;
}

export interface CollationRowTitle {
    schemaVersion: 1;
    labels: CollationRowTitleLabel[];
    origin: CollationRowTitleOrigin;
    provenance?: CollationRowTitleProvenance;
    createdAt: string;
    updatedAt: string;
    updatedBy?: string;
}

export interface AnchoredRowSceneMetadata {
    sceneId?: string;
    sceneNumber?: number;
    sceneOrdinal?: number;
    title?: string;
    description?: string;
    arabicTitle?: string;
    rowStart?: number;
    rowEnd?: number;
    rowCount?: number;
    isSceneStart?: boolean;
    source?: string;
    [key: string]: unknown;
}

export type RetiredMaterialDisposition = 'retained' | 'hidden';

export interface AnchoredRowRetiredMaterialDecision {
    witnessId: string;
    unitUuid: string;
    disposition: RetiredMaterialDisposition;
    kind: 'ignored' | 'artifact-only' | 'metadata-only';
    reason?: string;
    decidedAt: string;
}

export interface AnchoredRowMetadata {
    rowTitle?: CollationRowTitle;
    scene?: AnchoredRowSceneMetadata;
    retiredMaterial?: AnchoredRowRetiredMaterialDecision[];
    emptyRowReview?: {
        disposition: 'retained';
        decidedAt: string;
    };
    [key: string]: unknown;
}

/**
 * A row in the alignment registry.
 * Contains all units (from different witnesses) that are considered aligned.
 */
export interface AnchoredRow {
    id: string;                    // Auto-generated UUID
    chapterId: string;             // FK to chapter
    labelPrefix: string;           // User-editable prefix, e.g., "Lo", "Bab"
    labelNumber: number;           // Derived display number; refreshed from position order on load
    position: number;              // Sort order within chapter
    unitMappings: UnitMapping[];   // Units from each witness
    createdAt: string;             // ISO timestamp
    updatedAt: string;             // ISO timestamp
    createdBy?: string;            // User ID
    status: 'auto' | 'confirmed' | 'needs-review';
    metadata?: AnchoredRowMetadata;
}

/**
 * Serialized format for IndexedDB storage
 */
export interface AnchoredRowRecord {
    id: string;
    chapterId: string;
    labelPrefix: string;
    labelNumber: number;
    position: number;
    unitMappings: string;          // JSON stringified UnitMapping[]
    createdAt: string;
    updatedAt: string;
    createdBy?: string;
    status: 'auto' | 'confirmed' | 'needs-review';
    metadata?: AnchoredRowMetadata;
}

/**
 * Options for upserting rows
 */
export interface UpsertRowOptions {
    updatedBy?: string;
    autoRelabel?: boolean;         // Regenerate labels after upsert
    labelPrefix?: string;          // Chapter/workbench prefix to use when relabeling
    changedRowIds?: string[];       // Persist only these verified durable row changes
}

/**
 * Helper to generate row display label
 */
export function getRowLabel(row: AnchoredRow): string {
    return `${row.labelPrefix}-${row.labelNumber.toString().padStart(3, '0')}`;
}

/**
 * Serialize row for IndexedDB storage
 */
export function serializeAnchoredRow(row: AnchoredRow): AnchoredRowRecord {
    return {
        ...row,
        unitMappings: JSON.stringify(row.unitMappings)
    };
}

/**
 * Deserialize row from IndexedDB storage
 */
export function deserializeAnchoredRow(record: AnchoredRowRecord): AnchoredRow {
    return {
        ...record,
        unitMappings: JSON.parse(record.unitMappings)
    };
}
